import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import DetailsCard from "@/components/DetailsCard";
import SearchInput from "@/components/SearchInput";
import EmptyState from "@/components/EmptyState";
import SideSheetModal from "@/components/SideSheetModal";
import Tag, { type TagType } from "@/components/Tag/Tag";
import usePagination from "@/hooks/usePagination";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { useCanManageGrpCommissionBool } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate } from "@/router";
import CommissionTabLayout from "../_CommissionTabLayout";
import { formatEnumLabel } from "../_utils";
import { useCanShowCommissionPlatformBool, useRequireCommissionPlatform } from "../useCommissionPlatformGate";
import { COMMISSION_PARTNERS_QUERY } from "./queries";
import { getColumns } from "./_components/TableColumns";
import type { CommissionPartnerNode } from "./types";

function partnerStatusTagType(status: CommissionPartnerNode["status"]): TagType {
  return status === "ACTIVE" ? "success" : "surface";
}

export default function CommissionPartnersPage() {
  usePageTitle("/billing/finance/commission/partners", "Partners");
  useRequireCommissionPlatform();
  const canQuery = useCanShowCommissionPlatformBool();
  const [selectedPartner, setSelectedPartner] = useState<CommissionPartnerNode | null>(null);
  const navigate = useNavigate();
  const canManageCommission = useCanManageGrpCommissionBool();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const columns = useMemo(() => getColumns(), []);
  const tableActions = useMemo(
    () =>
      canManageCommission
        ? [
            {
              label: "Add partner",
              icon: "add" as const,
              onClick: () => navigate("/billing/finance/commission/partners/new")
            }
          ]
        : [],
    [canManageCommission, navigate]
  );

  useEffect(() => {
    resetPagination();
  }, [debouncedSearch, resetPagination]);

  const { data, previousData, loading, error } = useQuery(COMMISSION_PARTNERS_QUERY, {
    variables: {
      ...paginationVariables,
      ...(debouncedSearch ? { search: debouncedSearch } : {})
    },
    skip: !canQuery
  });

  const connection = data?.commissionPartners ?? previousData?.commissionPartners;
  const items = connection?.nodes ?? [];
  const totalRowCount = connection?.totalCount ?? items.length;
  const hasActiveFilters = !!debouncedSearch;

  if (error) {
    return (
      <CommissionTabLayout>
        <EmptyState title="Error" caption="An error occurred while loading partners." iconName="error" />
      </CommissionTabLayout>
    );
  }

  return (
    <CommissionTabLayout>
      <TableHeading body="Sponsor partners participating in commission programs." actions={tableActions} />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        onClickRow={(row) => setSelectedPartner(row.original)}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Partners Yet." caption="Partners will appear here once configured." iconName="list" />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results." caption="Try a different search." iconName="search" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            onSortClick={onSortClick}
            activeFilters={hasActiveFilters ? 1 : 0}
            onClearAllFilters={() => {
              clearSearch();
              resetPagination();
            }}
          >
            <SearchInput
              query={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                resetPagination();
              }}
              onClear={() => {
                clearSearch();
                resetPagination();
              }}
              placeholder="Search partner name"
            />
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            totalRowCount={totalRowCount}
          />
        )}
      />
      <SideSheetModal
        isSidebarOpen={!!selectedPartner}
        setSidebarOpen={(open) => {
          if (!open) setSelectedPartner(null);
        }}
        header={{ title: selectedPartner?.name ?? "Partner details" }}
        disableFocusManagement
      >
        {selectedPartner && (
          <DetailsCard
            compact
            heading="Details"
            options={[
              {
                overline: "Partner",
                headline: selectedPartner.name
              },
              {
                overline: "Legacy ID",
                headline: selectedPartner.legacyId ?? "—"
              },
              {
                overline: "Status",
                headline: formatEnumLabel(selectedPartner.status),
                status: () => (
                  <Tag tagType={partnerStatusTagType(selectedPartner.status)} tagStyle="strong">
                    {formatEnumLabel(selectedPartner.status)}
                  </Tag>
                )
              }
            ]}
          />
        )}
      </SideSheetModal>
    </CommissionTabLayout>
  );
}
